/**
 * ExternalRiskWidgets.jsx
 * 
 * Widgets for displaying External Risk Scan data in Custom Reports.
 * These widgets render data from domain security scans including:
 * - Overall risk score and domain info
 * - Category breakdowns (DNS, Email, SSL, Headers, etc.)
 * - Findings tables (all, warnings, passed)
 * - Charts (severity distribution, status, category breakdown)
 * - Compliance mapping
 */

import React from 'react';
import { Pie, Bar, Doughnut } from 'react-chartjs-2';
import './ProofTablesWidgets.css';
import './FindingsWidgets.css';
import { getLabel, getLabelStyle } from './widgetLabelHelpers';

// Helper function to calculate pagination slice indices
// Supports different itemsPerPage for first page vs continuation pages
const getPaginationSlice = (pagination) => {
    if (!pagination) return null;
    const startIndex = pagination.startIndex !== undefined
        ? pagination.startIndex
        : pagination.currentPage * pagination.itemsPerPage;
    const endIndex = pagination.endIndex !== undefined
        ? pagination.endIndex
        : startIndex + pagination.itemsPerPage;
    return { startIndex, endIndex };
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get external risk data from scan data object
 * Looks for data in multiple possible locations with extensive logging
 */
const getExternalRiskData = (data) => {
    // Debug: Log what we received
    console.log('[ExternalRiskWidgets] getExternalRiskData called with:', {
        dataType: typeof data,
        isNull: data === null,
        isUndefined: data === undefined,
        keys: data ? Object.keys(data).slice(0, 15) : []
    });

    if (!data) {
        console.log('[ExternalRiskWidgets] No data provided');
        return null;
    }

    // Check for 'external' namespace first (combined reports from ReportBuilderPage)
    if (data.external && typeof data.external === 'object') {
        console.log('[ExternalRiskWidgets] ✅ Found data at data.external');
        if (data.external.domain || data.external.categories) {
            return data.external;
        }
    }

    // Check for 'externalData' namespace (alternative naming)
    if (data.externalData && typeof data.externalData === 'object') {
        console.log('[ExternalRiskWidgets] ✅ Found data at data.externalData');
        if (data.externalData.domain || data.externalData.categories) {
            return data.externalData;
        }
    }

    // Check if data itself has external risk scan structure at root
    if (data.domain && data.categories && Array.isArray(data.categories)) {
        console.log('[ExternalRiskWidgets] ✅ Found data at root level');
        return data;
    }

    // Check data.data wrapper (common API response pattern)
    if (data.data && typeof data.data === 'object') {
        if (data.data.external) {
            console.log('[ExternalRiskWidgets] ✅ Found data at data.data.external');
            return data.data.external;
        }
        if (data.data.domain && data.data.categories) {
            console.log('[ExternalRiskWidgets] ✅ Found data at data.data');
            return data.data;
        }
    }

    // Check scanData wrapper
    if (data.scanData && typeof data.scanData === 'object') {
        if (data.scanData.external) {
            console.log('[ExternalRiskWidgets] ✅ Found data at data.scanData.external');
            return data.scanData.external;
        }
        if (data.scanData.domain && data.scanData.categories) {
            console.log('[ExternalRiskWidgets] ✅ Found data at data.scanData');
            return data.scanData;
        }
    }

    // Check metricsData wrapper 
    if (data.metricsData && typeof data.metricsData === 'object') {
        if (data.metricsData.external) {
            console.log('[ExternalRiskWidgets] ✅ Found data at data.metricsData.external');
            return data.metricsData.external;
        }
        if (data.metricsData.externalData) {
            console.log('[ExternalRiskWidgets] ✅ Found data at data.metricsData.externalData');
            return data.metricsData.externalData;
        }
    }

    // Deep search: look for any key containing 'external' or an object with 'domain' and 'categories'
    for (const key of Object.keys(data)) {
        const value = data[key];
        if (value && typeof value === 'object') {
            if (value.domain && value.categories && Array.isArray(value.categories)) {
                console.log(`[ExternalRiskWidgets] ✅ Found data at data.${key}`);
                return value;
            }
        }
    }

    console.log('[ExternalRiskWidgets] ❌ Could not find external risk data. Available keys:', Object.keys(data));
    return null;
};

/**
 * Get color for severity level
 */
const getSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
        case 'critical': return '#dc3545';
        case 'high': return '#fd7e14';
        case 'medium': return '#ffc107';
        case 'low': return '#28a745';
        case 'info': return '#17a2b8';
        default: return '#6c757d';
    }
};

/**
 * Get color for status
 */
const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
        case 'pass': return '#28a745';
        case 'warning': return '#ffc107';
        case 'fail': return '#dc3545';
        default: return '#6c757d';
    }
};

/**
 * Get all findings from all categories
 */
const getAllFindings = (data) => {
    if (!data?.categories) return [];

    const findings = [];
    data.categories.forEach(category => {
        if (category.findings) {
            category.findings.forEach(finding => {
                findings.push({
                    ...finding,
                    categoryName: category.name,
                    categorySeverity: category.severity
                });
            });
        }
    });
    return findings;
};

/**
 * Extract all unique compliance frameworks
 */
const getAllCompliance = (data) => {
    if (!data?.categories) return [];

    const complianceSet = new Set();
    const complianceDetails = [];

    data.categories.forEach(category => {
        category.findings?.forEach(finding => {
            finding.compliance?.forEach(comp => {
                if (!complianceSet.has(comp)) {
                    complianceSet.add(comp);
                    complianceDetails.push({
                        framework: comp,
                        finding: finding.title,
                        category: category.name,
                        status: finding.status
                    });
                }
            });
        });
    });

    return complianceDetails;
};

// ============================================================================
// EXTERNAL RISK SCORE WIDGET
// ============================================================================

export const ExternalRiskScoreWidget = ({ data, config = {}, style = {}, title }) => {
    const widgetType = 'external-risk-score';
    const displayTitle = title || config?.title;

    // Get customizable labels
    const scoreLabel = getLabel(config, widgetType, 'scoreLabel');

    // Get label styles
    const titleStyle = getLabelStyle(config, 'title', {
        fontSize: '16px',
        fontWeight: 'bold',
        color: '#333333',
        textAlign: 'left',
        marginBottom: '16px'
    }, widgetType);
    const scoreValueStyle = getLabelStyle(config, 'scoreValue', {
        fontSize: '48px',
        fontWeight: '700',
        textAlign: 'center'
    }, widgetType);
    const scoreLabelStyle = getLabelStyle(config, 'scoreLabel', {
        fontSize: '14px',
        color: '#6c757d',
        textAlign: 'center'
    }, widgetType);

    const externalData = getExternalRiskData(data);

    if (!externalData) {
        return null;
    }

    const { riskScore = 0, overallRisk = 'unknown' } = externalData;

    // Determine color based on score (inverted - lower is better for risk)
    const getScoreColor = (score) => {
        if (score >= 70) return '#28a745'; // Green - good security
        if (score >= 40) return '#ffc107'; // Yellow - moderate
        return '#dc3545'; // Red - needs attention
    };

    const scoreColor = getScoreColor(riskScore);

    // Dynamic alignment for Pill based on score value alignment
    const textAlign = scoreValueStyle.textAlign || 'center';
    let pillAlignment = 'center';
    if (textAlign === 'left') pillAlignment = 'flex-start';
    if (textAlign === 'right') pillAlignment = 'flex-end';

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            padding: '24px',
            background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
            borderRadius: '12px',
            ...style
        }}>
            {displayTitle && (
                <h3 style={titleStyle}>{displayTitle}</h3>
            )}
            <div style={{
                fontSize: '48px',
                fontWeight: '700',
                lineHeight: 1,
                ...scoreValueStyle,
                color: scoreValueStyle.color || scoreColor
            }}>
                {riskScore}%
            </div>
            <div style={{
                fontSize: '14px',
                color: '#6c757d',
                marginTop: '8px',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                ...scoreLabelStyle
            }}>
                {scoreLabel}
            </div>
            <div style={{
                marginTop: '12px',
                padding: '4px 12px',
                borderRadius: '20px',
                backgroundColor: scoreColor,
                color: '#fff',
                fontSize: '12px',
                fontWeight: '600',
                textTransform: 'uppercase',
                width: 'fit-content',
                alignSelf: pillAlignment
            }}>
                {overallRisk} Risk
            </div>
        </div>
    );
};

// ============================================================================
// EXTERNAL DOMAIN INFO WIDGET
// ============================================================================

export const ExternalDomainInfoWidget = ({ data, config = {}, style = {}, title }) => {
    const widgetType = 'external-domain-info';
    const displayTitle = title || config?.title;

    // Get configurable labels
    const domainLabel = getLabel(config, widgetType, 'domainLabel');
    const scanDateLabel = getLabel(config, widgetType, 'scanDateLabel'); // Note: Added to definition?

    // Get label styles
    const titleStyle = getLabelStyle(config, 'title', {
        fontSize: '16px',
        fontWeight: 'bold',
        color: '#333333',
        textAlign: 'left'
    }, widgetType);
    const labelStyle = getLabelStyle(config, 'labels', {
        fontSize: '12px',
        color: '#6c757d'
    }, widgetType);
    const valueStyle = getLabelStyle(config, 'values', {
        fontSize: '14px',
        fontWeight: '600',
        color: '#212529'
    }, widgetType);

    const externalData = getExternalRiskData(data);

    if (!externalData) {
        return null;
    }

    const { domain, timestamp, criticalCount, passedCount } = externalData;
    const scanDate = timestamp ? new Date(timestamp).toLocaleString() : 'Unknown';

    return (
        <div style={{
            padding: '20px',
            backgroundColor: '#fff',
            borderRadius: '8px',
            border: '1px solid #e9ecef',
            ...style
        }}>
            <h4 style={titleStyle}>
                {displayTitle || '🌐 Domain Information'}
            </h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                <div>
                    <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '4px', ...labelStyle }}>{domainLabel}</div>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#212529', ...valueStyle }}>{domain || 'N/A'}</div>
                </div>
                <div>
                    <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '4px', ...labelStyle }}>{scanDateLabel}</div>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#212529', ...valueStyle }}>{scanDate}</div>
                </div>
                <div>
                    <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '4px', ...labelStyle }}>Critical Issues</div>
                    <div style={{ fontSize: '14px', fontWeight: '600', ...valueStyle, color: valueStyle.color || (criticalCount > 0 ? '#dc3545' : '#28a745') }}>
                        {criticalCount || 0}
                    </div>
                </div>
                <div>
                    <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '4px', ...labelStyle }}>Passed Checks</div>
                    <div style={{ fontSize: '14px', fontWeight: '600', ...valueStyle, color: valueStyle.color || '#28a745' }}>{passedCount || 0}</div>
                </div>
            </div>
        </div>
    );
};

// ============================================================================
// EXTERNAL RISK OVERVIEW WIDGET
// ============================================================================

export const ExternalRiskOverviewWidget = ({ data, config = {}, style = {}, title }) => {
    const widgetType = 'external-risk-overview';
    const displayTitle = title || config?.title;

    // Get configurable labels
    const totalLabel = getLabel(config, widgetType, 'totalLabel');
    const criticalLabel = getLabel(config, widgetType, 'criticalLabel');
    const warningsLabel = getLabel(config, widgetType, 'warningsLabel');
    const passedLabel = getLabel(config, widgetType, 'passedLabel');

    // Get label styles
    const titleStyle = getLabelStyle(config, 'title', {
        fontSize: '16px',
        fontWeight: 'bold',
        color: '#333333',
        textAlign: 'left'
    }, widgetType);
    const cardLabelStyle = getLabelStyle(config, 'cardLabels', {
        fontSize: '12px',
        color: '#6c757d'
    }, widgetType);
    const cardValueStyle = getLabelStyle(config, 'cardValues', {
        fontSize: '24px',
        fontWeight: '700'
    }, widgetType);

    const externalData = getExternalRiskData(data);

    if (!externalData) {
        return null;
    }

    const { riskScore, overallRisk, criticalCount, passedCount } = externalData;
    const totalFindings = getAllFindings(externalData).length;
    const warningCount = getAllFindings(externalData).filter(f => f.status === 'warning').length;

    const scoreLabel = getLabel(config, widgetType, 'scoreLabel');
    const riskLevelLabel = getLabel(config, widgetType, 'riskLevelLabel');

    const cards = [
        { label: scoreLabel, value: `${riskScore}%`, color: riskScore >= 70 ? '#28a745' : riskScore >= 40 ? '#ffc107' : '#dc3545' },
        { label: riskLevelLabel, value: overallRisk?.toUpperCase() || 'N/A', color: '#6c757d' },
        { label: criticalLabel, value: criticalCount || 0, color: '#dc3545' },
        { label: warningsLabel, value: warningCount, color: '#ffc107' },
        { label: passedLabel, value: passedCount || 0, color: '#28a745' },
        { label: totalLabel, value: totalFindings, color: '#17a2b8' }
    ];

    return (
        <div style={style}>
            {displayTitle && (
                <h3 style={titleStyle}>{displayTitle}</h3>
            )}
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                gap: '12px'
            }}>
                {cards.map((card, idx) => (
                    <div key={idx} style={{
                        padding: '16px',
                        backgroundColor: '#fff',
                        borderRadius: '8px',
                        border: '1px solid #e9ecef',
                        textAlign: 'center'
                    }}>
                        <div style={{ fontSize: '24px', fontWeight: '700', ...cardValueStyle, color: cardValueStyle.color || card.color }}>
                            {card.value}
                        </div>
                        <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '4px', ...cardLabelStyle }}>
                            {card.label}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

// ============================================================================
// EXTERNAL CATEGORY BREAKDOWN WIDGET
// ============================================================================

export const ExternalCategoryBreakdownWidget = ({ data, config = {}, style = {}, title }) => {
    const widgetType = 'external-category-breakdown';
    const displayTitle = title || config?.title;

    // Get configurable labels
    // Get label styles
    const titleStyle = getLabelStyle(config, 'title', {
        fontSize: '16px',
        fontWeight: 'bold',
        color: '#333333',
        textAlign: 'left'
    }, widgetType);
    const catNameStyle = getLabelStyle(config, 'categoryNames', {
        fontSize: '14px',
        fontWeight: '600',
        color: '#212529'
    }, widgetType);
    const catDescStyle = getLabelStyle(config, 'categoryDescriptions', {
        fontSize: '12px',
        color: '#6c757d'
    }, widgetType);

    const externalData = getExternalRiskData(data);

    if (!externalData?.categories) {
        return null;
    }

    return (
        <div style={style}>
            {displayTitle && (
                <h3 style={titleStyle}>{displayTitle}</h3>
            )}
            {externalData.categories.map((category, idx) => {
                const passCount = category.findings?.filter(f => f.status === 'pass').length || 0;
                const warnCount = category.findings?.filter(f => f.status === 'warning').length || 0;
                const totalCount = category.findings?.length || 0;

                return (
                    <div key={idx} style={{
                        padding: '16px',
                        backgroundColor: '#fff',
                        borderRadius: '8px',
                        border: '1px solid #e9ecef',
                        marginBottom: '12px'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                                <h5 style={{ margin: 0, fontSize: '14px', color: '#212529', ...catNameStyle }}>{category.name}</h5>
                                <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#6c757d', ...catDescStyle }}>
                                    {category.description}
                                </p>
                            </div>
                            <div style={{
                                display: 'flex',
                                gap: '8px'
                            }}>
                                <span style={{
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    backgroundColor: getSeverityColor(category.severity),
                                    color: '#fff',
                                    fontSize: '11px',
                                    fontWeight: '600'
                                }}>
                                    {category.severity?.toUpperCase()}
                                </span>
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: '16px', marginTop: '12px' }}>
                            <span style={{ fontSize: '12px' }}>
                                <span style={{ color: '#28a745' }}>✓</span> {passCount} passed
                            </span>
                            <span style={{ fontSize: '12px' }}>
                                <span style={{ color: '#ffc107' }}>⚠</span> {warnCount} warnings
                            </span>
                            <span style={{ fontSize: '12px', color: '#6c757d' }}>
                                {totalCount} total
                            </span>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

// ============================================================================
// EXTERNAL ALL FINDINGS TABLE WIDGET
// ============================================================================

export const ExternalAllFindingsTableWidget = ({ data, config = {}, style = {}, pagination, title }) => {
    const displayTitle = title || config?.title || 'All External Findings';
    const widgetType = 'external-all-findings-table';

    const externalData = getExternalRiskData(data);
    const allFindings = getAllFindings(externalData);

    // Apply pagination if provided
    const displayFindings = pagination
        ? (() => {
            const slice = getPaginationSlice(pagination);
            return allFindings.slice(slice.startIndex, slice.endIndex);
        })()
        : allFindings;

    // Get customizable labels
    const rawColumnHeaders = getLabel(config, widgetType, 'columnHeaders');
    const columnHeaders = Array.isArray(rawColumnHeaders) ? rawColumnHeaders : (
        widgetType === 'external-all-findings-table' ? ['Category', 'Finding', 'Status', 'Severity', 'Value'] : []
    );

    // Get label styles
    const headerStyle = getLabelStyle(config, 'columnHeaders', {
        fontSize: '12px',
        fontWeight: '600',
        color: '#333333',
        textAlign: 'left'
    }, widgetType);
    const dataRowStyle = getLabelStyle(config, 'dataRows', {
        fontSize: '14px',
        fontWeight: '400',
        color: '#212529',
        textAlign: 'left'
    }, widgetType);
    const titleStyle = getLabelStyle(config, 'title', {
        fontSize: '18px',
        fontWeight: '700',
        color: '#333333',
        textAlign: 'left'
    }, widgetType);

    // Grid column template: Category, Finding, Status, Severity, Value
    const templateColumns = '1.2fr 2.5fr 0.8fr 0.8fr 1.5fr';

    if (displayFindings.length === 0) {
        return null;
    }

    return (
        <div className="open-ports-widget" style={style}>
            {displayTitle && <h3 className="findings-widget-title" style={titleStyle}>{displayTitle}</h3>}

            <div className="open-ports-table">
                {/* Header Row */}
                <div className="open-ports-header" style={{ gridTemplateColumns: templateColumns }}>
                    {columnHeaders.map((header, idx) => (
                        <div key={idx} style={headerStyle}>{header}</div>
                    ))}
                </div>

                {/* Data Rows */}
                <div className="open-ports-body">
                    {displayFindings.map((finding, idx) => (
                        <div key={idx} className="open-ports-row" style={{ gridTemplateColumns: templateColumns }}>
                            <div className="port-service" style={dataRowStyle}>{finding.categoryName}</div>
                            <div className="port-service" style={dataRowStyle}>
                                <div style={{ fontWeight: dataRowStyle.fontWeight || '600', fontSize: dataRowStyle.fontSize, color: dataRowStyle.color }}>{finding.title}</div>
                                <div style={{ color: dataRowStyle.color || '#6c757d', fontSize: dataRowStyle.fontSize || '11px', marginTop: '4px' }}>
                                    {finding.description}
                                </div>
                            </div>
                            <div className="port-number">
                                <span style={{
                                    padding: '2px 8px',
                                    borderRadius: '4px',
                                    backgroundColor: getStatusColor(finding.status),
                                    color: '#fff',
                                    fontSize: '11px'
                                }}>
                                    {finding.status?.toUpperCase()}
                                </span>
                            </div>
                            <div className="port-number">
                                <span style={{
                                    padding: '2px 8px',
                                    borderRadius: '4px',
                                    backgroundColor: getSeverityColor(finding.severity),
                                    color: '#fff',
                                    fontSize: '11px'
                                }}>
                                    {finding.severity?.toUpperCase()}
                                </span>
                            </div>
                            <div className="port-service" style={{ maxWidth: '200px', wordBreak: 'break-word', ...dataRowStyle }}>
                                {finding.value}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

// ============================================================================
// EXTERNAL WARNINGS TABLE WIDGET
// ============================================================================

export const ExternalWarningsTableWidget = ({ data, config = {}, style = {}, pagination, title }) => {
    const displayTitle = title || config?.title || 'Warnings & Issues';
    const widgetType = 'external-warnings-table';

    // Get customizable labels
    const rawColumnHeaders = getLabel(config, widgetType, 'columnHeaders');
    const columnHeaders = Array.isArray(rawColumnHeaders) ? rawColumnHeaders : (
        widgetType === 'external-warnings-table' ? ['Category', 'Finding', 'Severity', 'Value'] : []
    );

    // Get label styles
    const titleStyle = getLabelStyle(config, 'title', {
        fontSize: '18px',
        fontWeight: '700',
        color: '#333333',
        textAlign: 'left'
    }, widgetType);
    const headerStyle = getLabelStyle(config, 'columnHeaders', {
        fontSize: '12px',
        fontWeight: '600',
        color: '#333333',
        textAlign: 'left'
    }, widgetType);
    const dataRowStyle = getLabelStyle(config, 'dataRows', {
        fontSize: '14px',
        fontWeight: '400',
        color: '#212529',
        textAlign: 'left'
    }, widgetType);

    const externalData = getExternalRiskData(data);
    const allFindings = getAllFindings(externalData);
    const warnings = allFindings.filter(f => f.status === 'warning' || f.status === 'fail');

    // Apply pagination if provided
    const displayWarnings = pagination
        ? (() => {
            const slice = getPaginationSlice(pagination);
            return warnings.slice(slice.startIndex, slice.endIndex);
        })()
        : warnings;

    // Grid column template: Category, Issue, Severity, Recommendation
    const templateColumns = '1.2fr 2.5fr 0.8fr 1.5fr';

    if (displayWarnings.length === 0) {
        return null;
    }

    return (
        <div className="open-ports-widget" style={style}>
            {displayTitle && <h3 className="findings-widget-title" style={titleStyle}>{displayTitle}</h3>}

            <div className="open-ports-table">
                {/* Header Row */}
                <div className="open-ports-header" style={{ gridTemplateColumns: templateColumns }}>
                    {columnHeaders.map((header, idx) => (
                        <div key={idx} style={headerStyle}>{header}</div>
                    ))}
                </div>

                {/* Data Rows */}
                <div className="open-ports-body">
                    {displayWarnings.map((finding, idx) => (
                        <div key={idx} className="open-ports-row" style={{ gridTemplateColumns: templateColumns }}>
                            <div className="port-service" style={dataRowStyle}>{finding.categoryName}</div>
                            <div className="port-service" style={dataRowStyle}>
                                <div style={{ fontWeight: dataRowStyle.fontWeight || '600', fontSize: dataRowStyle.fontSize, color: dataRowStyle.color }}>{finding.title}</div>
                                <div style={{ color: dataRowStyle.color || '#6c757d', fontSize: dataRowStyle.fontSize || '11px', marginTop: '4px' }}>
                                    {finding.description}
                                </div>
                            </div>
                            <div className="port-number">
                                <span style={{
                                    padding: '2px 8px',
                                    borderRadius: '4px',
                                    backgroundColor: getSeverityColor(finding.severity),
                                    color: '#fff',
                                    fontSize: dataRowStyle.fontSize || '11px',
                                    fontWeight: dataRowStyle.fontWeight
                                }}>
                                    {finding.severity?.toUpperCase()}
                                </span>
                            </div>
                            <div className="port-service" style={{ maxWidth: '200px', wordBreak: 'break-word', ...dataRowStyle }}>
                                {finding.value}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

// ============================================================================
// EXTERNAL PASSED TABLE WIDGET
// ============================================================================

export const ExternalPassedTableWidget = ({ data, config = {}, style = {}, pagination, title }) => {
    const displayTitle = title || config?.title || 'Passed Checks';
    const widgetType = 'external-passed-table';

    // Get customizable labels
    const rawColumnHeaders = getLabel(config, widgetType, 'columnHeaders');
    const columnHeaders = Array.isArray(rawColumnHeaders) ? rawColumnHeaders : (
        // Fallback to defaults if not an array
        widgetType === 'external-passed-table' ? ['Category', 'Finding', 'Value'] : []
    );

    // Get label styles
    const titleStyle = getLabelStyle(config, 'title', {
        fontSize: '18px',
        fontWeight: '700',
        color: '#333333',
        textAlign: 'left'
    }, widgetType);
    const headerStyle = getLabelStyle(config, 'columnHeaders', {
        fontSize: '12px',
        fontWeight: '600',
        color: '#333333',
        textAlign: 'left'
    }, widgetType);
    const dataRowStyle = getLabelStyle(config, 'dataRows', {
        fontSize: '14px',
        fontWeight: '400',
        color: '#212529',
        textAlign: 'left'
    }, widgetType);

    const externalData = getExternalRiskData(data);
    const allFindings = getAllFindings(externalData);
    const passed = allFindings.filter(f => f.status === 'pass');

    // Apply pagination if provided
    const displayPassed = pagination
        ? (() => {
            const slice = getPaginationSlice(pagination);
            return passed.slice(slice.startIndex, slice.endIndex);
        })()
        : passed;

    // Grid column template: Category, Check, Details
    const templateColumns = '1.2fr 2.5fr 2fr';

    if (displayPassed.length === 0) {
        return null;
    }

    return (
        <div className="open-ports-widget" style={style}>
            {displayTitle && <h3 className="findings-widget-title" style={titleStyle}>{displayTitle}</h3>}

            <div className="open-ports-table">
                {/* Header Row */}
                <div className="open-ports-header" style={{ gridTemplateColumns: templateColumns }}>
                    {columnHeaders.map((header, idx) => (
                        <div key={idx} style={headerStyle}>{header}</div>
                    ))}
                </div>

                {/* Data Rows */}
                <div className="open-ports-body">
                    {displayPassed.map((finding, idx) => (
                        <div key={idx} className="open-ports-row" style={{ gridTemplateColumns: templateColumns }}>
                            <div className="port-service" style={dataRowStyle}>{finding.categoryName}</div>
                            <div className="port-service" style={dataRowStyle}>
                                <div style={{ fontWeight: dataRowStyle.fontWeight || '600', fontSize: dataRowStyle.fontSize, color: dataRowStyle.color }}>✓ {finding.title}</div>
                                <div style={{ color: dataRowStyle.color || '#6c757d', fontSize: dataRowStyle.fontSize || '11px', marginTop: '4px' }}>
                                    {finding.description}
                                </div>
                            </div>
                            <div className="port-service" style={{ maxWidth: '200px', wordBreak: 'break-word', ...dataRowStyle }}>
                                {finding.value}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

// ============================================================================
// EXTERNAL SEVERITY PIE CHART WIDGET
// ============================================================================

export const ExternalSeverityPieChartWidget = ({ data, config = {}, style = {}, title }) => {
    const displayTitle = title || config?.title;
    const widgetType = 'external-severity-pie-chart';

    // Get label styles
    const titleStyle = getLabelStyle(config, 'title', {
        fontSize: '16px',
        fontWeight: 'bold',
        color: '#333333',
        textAlign: 'left'
    }, widgetType);

    const externalData = getExternalRiskData(data);
    const allFindings = getAllFindings(externalData);

    if (allFindings.length === 0) {
        return null;
    }

    // Count by severity
    const severityCounts = {
        critical: 0,
        high: 0,
        medium: 0,
        low: 0,
        info: 0
    };

    allFindings.forEach(f => {
        const sev = f.severity?.toLowerCase() || 'info';
        if (severityCounts[sev] !== undefined) {
            severityCounts[sev]++;
        }
    });

    const chartData = {
        labels: ['Critical', 'High', 'Medium', 'Low', 'Info'],
        datasets: [{
            data: [severityCounts.critical, severityCounts.high, severityCounts.medium, severityCounts.low, severityCounts.info],
            backgroundColor: ['#dc3545', '#fd7e14', '#ffc107', '#28a745', '#17a2b8'],
            borderWidth: 0
        }]
    };

    return (
        <div style={style}>
            {displayTitle && (
                <h3 style={titleStyle}>{displayTitle}</h3>
            )}
            <div style={{ width: '100%', height: '250px' }}>
                <Pie
                    data={chartData}
                    options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                            legend: { 
                                display: config.legendPosition !== 'none',
                                position: config.legendPosition || 'top', 
                                labels: { font: { size: 11 } } 
                            }
                        }
                    }}
                />
            </div>
        </div>
    );
};

// ============================================================================
// EXTERNAL STATUS PIE CHART WIDGET
// ============================================================================

export const ExternalStatusPieChartWidget = ({ data, config = {}, style = {}, title }) => {
    const displayTitle = title || config?.title;
    const widgetType = 'external-status-pie-chart';

    // Get label styles
    const titleStyle = getLabelStyle(config, 'title', {
        fontSize: '16px',
        fontWeight: 'bold',
        color: '#333333',
        textAlign: 'left'
    }, widgetType);

    const externalData = getExternalRiskData(data);
    const allFindings = getAllFindings(externalData);

    if (allFindings.length === 0) {
        return null;
    }

    const statusCounts = {
        pass: 0,
        warning: 0,
        fail: 0
    };

    allFindings.forEach(f => {
        const status = f.status?.toLowerCase() || 'pass';
        if (statusCounts[status] !== undefined) {
            statusCounts[status]++;
        }
    });

    const chartData = {
        labels: ['Passed', 'Warning', 'Failed'],
        datasets: [{
            data: [statusCounts.pass, statusCounts.warning, statusCounts.fail],
            backgroundColor: ['#28a745', '#ffc107', '#dc3545'],
            borderWidth: 2,
            borderColor: '#fff'
        }]
    };

    return (
        <div style={style}>
            {displayTitle && (
                <h3 style={titleStyle}>{displayTitle}</h3>
            )}
            <div style={{ width: '100%', height: '250px' }}>
                <Doughnut
                    data={chartData}
                    options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        cutout: '60%',
                        plugins: {
                            legend: { 
                                display: config.legendPosition !== 'none',
                                position: config.legendPosition || 'top', 
                                labels: { font: { size: 11 } } 
                            }
                        }
                    }}
                />
            </div>
        </div>
    );
};

// ============================================================================
// EXTERNAL CATEGORY BAR CHART WIDGET
// ============================================================================

export const ExternalCategoryBarChartWidget = ({ data, config = {}, style = {}, title }) => {
    const displayTitle = title || config?.title;
    const widgetType = 'external-category-bar-chart';

    // Get label styles
    const titleStyle = getLabelStyle(config, 'title', {
        fontSize: '16px',
        fontWeight: 'bold',
        color: '#333333',
        textAlign: 'left'
    }, widgetType);

    const externalData = getExternalRiskData(data);

    if (!externalData?.categories) {
        return null;
    }

    const chartData = {
        labels: externalData.categories.map(c => c.name.length > 20 ? c.name.substring(0, 20) + '...' : c.name),
        datasets: [
            {
                label: 'Passed',
                data: externalData.categories.map(c => c.findings?.filter(f => f.status === 'pass').length || 0),
                backgroundColor: '#28a745'
            },
            {
                label: 'Warnings',
                data: externalData.categories.map(c => c.findings?.filter(f => f.status === 'warning').length || 0),
                backgroundColor: '#ffc107'
            },
            {
                label: 'Failed',
                data: externalData.categories.map(c => c.findings?.filter(f => f.status === 'fail').length || 0),
                backgroundColor: '#dc3545'
            }
        ]
    };

    return (
        <div style={style}>
            {displayTitle && (
                <h3 style={titleStyle}>{displayTitle}</h3>
            )}
            <div style={{ width: '100%', height: '300px' }}>
                <Bar
                    data={chartData}
                    options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                            legend: { 
                                display: config.legendPosition !== 'none',
                                position: config.legendPosition || 'top', 
                                labels: { font: { size: 11 } } 
                            }
                        },
                        scales: {
                            x: { stacked: true, ticks: { font: { size: 10 } } },
                            y: { stacked: true, beginAtZero: true }
                        }
                    }}
                />
            </div>
        </div>
    );
};

// ============================================================================
// EXTERNAL COMPLIANCE SUMMARY WIDGET
// ============================================================================

export const ExternalComplianceSummaryWidget = ({ data, config = {}, style = {}, title }) => {
    const displayTitle = title || config?.title;
    const widgetType = 'external-compliance-summary';

    // Get label styles
    const titleStyle = getLabelStyle(config, 'title', {
        fontSize: '16px',
        fontWeight: 'bold',
        color: '#333333',
        textAlign: 'left'
    }, widgetType);
    const frameworkNameStyle = getLabelStyle(config, 'frameworkNames', {
        fontSize: '14px',
        fontWeight: '600',
        color: '#212529'
    }, widgetType);

    const externalData = getExternalRiskData(data);
    const complianceDetails = getAllCompliance(externalData);

    if (complianceDetails.length === 0) {
        return null;
    }

    // Group by framework prefix (e.g., "NIST 800-53", "CIS Controls")
    const frameworkGroups = {};
    complianceDetails.forEach(item => {
        const parts = item.framework.split(':');
        const framework = parts[0].trim();
        if (!frameworkGroups[framework]) {
            frameworkGroups[framework] = [];
        }
        frameworkGroups[framework].push(item);
    });

    return (
        <div style={style}>
            {displayTitle && (
                <h3 style={titleStyle}>{displayTitle}</h3>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                {Object.keys(frameworkGroups).map((framework, idx) => (
                    <div key={idx} style={{
                        padding: '16px',
                        backgroundColor: '#fff',
                        borderRadius: '8px',
                        border: '1px solid #e9ecef'
                    }}>
                        <div style={{ fontWeight: '600', marginBottom: '8px', color: '#212529', ...frameworkNameStyle }}>{framework}</div>
                        <div style={{ fontSize: '13px', color: '#6c757d' }}>
                            {frameworkGroups[framework].length} references
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

// ============================================================================
// EXTERNAL COMPLIANCE TABLE WIDGET
// ============================================================================

export const ExternalComplianceTableWidget = ({ data, config = {}, style = {}, pagination, title }) => {
    const displayTitle = title || config?.title || 'Compliance Mapping';
    const widgetType = 'external-compliance-table';

    // Get customizable labels
    const rawColumnHeaders = getLabel(config, widgetType, 'columnHeaders');
    const columnHeaders = Array.isArray(rawColumnHeaders) ? rawColumnHeaders : (
        widgetType === 'external-compliance-table' ? ['Compliance Framework', 'Category', 'Finding', 'Status'] : []
    );

    // Get label styles
    const titleStyle = getLabelStyle(config, 'title', {
        fontSize: '18px',
        fontWeight: '700',
        color: '#333333',
        textAlign: 'left'
    }, widgetType);
    const headerStyle = getLabelStyle(config, 'columnHeaders', {
        fontSize: '12px',
        fontWeight: '600',
        color: '#333333',
        textAlign: 'left'
    }, widgetType);
    const dataRowStyle = getLabelStyle(config, 'dataRows', {
        fontSize: '14px',
        fontWeight: '400',
        color: '#212529',
        textAlign: 'left'
    }, widgetType);

    const externalData = getExternalRiskData(data);
    const complianceDetails = getAllCompliance(externalData);

    // Apply pagination if provided
    const displayItems = pagination
        ? (() => {
            const slice = getPaginationSlice(pagination);
            return complianceDetails.slice(slice.startIndex, slice.endIndex);
        })()
        : complianceDetails;

    // Grid column template: Compliance Framework, Category, Finding, Status
    const templateColumns = '1.5fr 1.2fr 2.5fr 0.8fr';

    if (displayItems.length === 0) {
        return null;
    }

    return (
        <div className="open-ports-widget" style={style}>
            {displayTitle && <h3 className="findings-widget-title" style={titleStyle}>{displayTitle}</h3>}

            <div className="open-ports-table">
                {/* Header Row */}
                <div className="open-ports-header" style={{ gridTemplateColumns: templateColumns }}>
                    {columnHeaders.map((header, idx) => (
                        <div key={idx} style={headerStyle}>{header}</div>
                    ))}
                </div>

                {/* Data Rows */}
                <div className="open-ports-body">
                    {displayItems.map((item, idx) => (
                        <div key={idx} className="open-ports-row" style={{ gridTemplateColumns: templateColumns }}>
                            <div className="port-service" style={{ fontWeight: '500', ...dataRowStyle }}>{item.framework}</div>
                            <div className="port-service" style={dataRowStyle}>{item.category}</div>
                            <div className="port-service" style={dataRowStyle}>{item.finding}</div>
                            <div className="port-number">
                                <span style={{
                                    padding: '2px 8px',
                                    borderRadius: '4px',
                                    backgroundColor: getStatusColor(item.status),
                                    color: '#fff',
                                    fontSize: dataRowStyle.fontSize || '11px',
                                    fontWeight: dataRowStyle.fontWeight
                                }}>
                                    {item.status?.toUpperCase()}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

// ============================================================================
// CATEGORY-SPECIFIC FINDINGS WIDGETS
// ============================================================================

const CategoryFindingsWidget = ({ data, categoryPattern, title, icon, style, customTitle, config = {}, widgetType }) => {
    // Get customizable labels
    // Note: title is typically handled by customTitle or passed title, but we can allow override via label system if config has it
    // The wrapper passes config.title as customTitle usually.
    // We'll stick to 'title' from definitions if available, or fallback.
    // Actually, getLabel for 'title' handles config.labels.title or definition default.
    // But here we have dynamic defaults (Icon + Name).

    // Get label styles
    const titleStyle = getLabelStyle(config, 'title', {
        fontSize: '14px',
        fontWeight: 'bold',
        color: '#212529'
    }, widgetType);
    const descStyle = getLabelStyle(config, 'description', {
        fontSize: '12px',
        color: '#6c757d'
    }, widgetType);
    const findingTitleStyle = getLabelStyle(config, 'findingTitles', {
        fontSize: '13px',
        fontWeight: '600'
    }, widgetType);
    const findingDescStyle = getLabelStyle(config, 'findingDescriptions', {
        fontSize: '11px',
        color: '#6c757d'
    }, widgetType);
    const findingValueStyle = getLabelStyle(config, 'findingValues', {
        fontSize: '11px',
        color: '#495057',
        fontFamily: 'monospace'
    }, widgetType);

    const externalData = getExternalRiskData(data);

    if (!externalData?.categories) {
        return null;
    }

    const category = externalData.categories.find(c =>
        c.name.toLowerCase().includes(categoryPattern.toLowerCase())
    );

    if (!category) {
        return null;
    }

    // Use customTitle if provided (from config.title), otherwise use category name with icon
    // But we also want to respect the 'title' label from settings if the user edited it there?
    // 'getLabel' generally assumes the default is static. Here the default is dynamic or passed as prop.
    // If we want the label system 'title' to override, we should use it.
    // However, the label system 'title' default is set in definitions (e.g., "📧 Email Security").
    // Let's use getLabel for title directly.
    // Use config.title if present (user override in properties panel), otherwise use passed title (from definition), otherwise blank
    const labelTitle = getLabel(config, widgetType, 'title');
    const displayTitle = config?.title || title || labelTitle;

    return (
        <div style={style}>
            <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#212529', ...titleStyle }}>
                {displayTitle}
            </h4>
            <p style={{ margin: '0 0 16px 0', fontSize: '12px', color: '#6c757d', ...descStyle }}>
                {category.description}
            </p>
            {category.findings?.map((finding, idx) => (
                <div key={idx} style={{
                    padding: '12px',
                    backgroundColor: '#fff',
                    borderLeft: `4px solid ${getStatusColor(finding.status)}`,
                    marginBottom: '8px',
                    borderRadius: '0 4px 4px 0'
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div>
                            <div style={{ fontWeight: '600', fontSize: '13px', ...findingTitleStyle }}>{finding.title}</div>
                            <div style={{ fontSize: '11px', color: '#6c757d', marginTop: '4px', ...findingDescStyle }}>{finding.description}</div>
                        </div>
                        <span style={{
                            padding: '2px 8px',
                            borderRadius: '4px',
                            backgroundColor: getStatusColor(finding.status),
                            color: '#fff',
                            fontSize: '10px',
                            flexShrink: 0,
                            marginLeft: '8px'
                        }}>
                            {finding.status?.toUpperCase()}
                        </span>
                    </div>
                    <div style={{ marginTop: '8px', fontSize: '11px', color: '#495057', fontFamily: 'monospace', ...findingValueStyle }}>
                        {finding.value}
                    </div>
                </div>
            ))}
        </div>
    );
};

export const ExternalDNSFindingsWidget = ({ data, config = {}, style }) => (
    <CategoryFindingsWidget
        data={data}
        config={config}
        widgetType="external-dns-findings"
        categoryPattern="dns"
        title="Domain & DNS Configuration"
        icon="🔗"
        style={style}
    />
);

export const ExternalEmailFindingsWidget = ({ data, config = {}, style }) => (
    <CategoryFindingsWidget
        data={data}
        config={config}
        widgetType="external-email-findings"
        categoryPattern="email"
        title="Email Security"
        icon="📧"
        style={style}
    />
);

export const ExternalSSLFindingsWidget = ({ data, config = {}, style }) => (
    <CategoryFindingsWidget
        data={data}
        config={config}
        widgetType="external-ssl-findings"
        categoryPattern="ssl"
        title="SSL/TLS"
        icon="🔒"
        style={style}
    />
);

export const ExternalHeadersFindingsWidget = ({ data, config = {}, style }) => (
    <CategoryFindingsWidget
        data={data}
        config={config}
        widgetType="external-headers-findings"
        categoryPattern="header"
        title="Security Headers"
        icon="🛡️"
        style={style}
    />
);

export const ExternalInfrastructureFindingsWidget = ({ data, config = {}, style }) => (
    <CategoryFindingsWidget
        data={data}
        config={config}
        widgetType="external-infrastructure-findings"
        categoryPattern="infrastructure"
        title="Infrastructure"
        icon="☁️"
        style={style}
    />
);

export const ExternalBreachFindingsWidget = ({ data, config = {}, style }) => (
    <CategoryFindingsWidget
        data={data}
        config={config}
        widgetType="external-breach-findings"
        categoryPattern="breach"
        title="Breach Intelligence"
        icon="🔓"
        style={style}
    />
);

// ============================================================================
// EXPORTS
// ============================================================================

export default {
    ExternalRiskScoreWidget,
    ExternalDomainInfoWidget,
    ExternalRiskOverviewWidget,
    ExternalCategoryBreakdownWidget,
    ExternalAllFindingsTableWidget,
    ExternalWarningsTableWidget,
    ExternalPassedTableWidget,
    ExternalSeverityPieChartWidget,
    ExternalStatusPieChartWidget,
    ExternalCategoryBarChartWidget,
    ExternalComplianceSummaryWidget,
    ExternalComplianceTableWidget,
    ExternalDNSFindingsWidget,
    ExternalEmailFindingsWidget,
    ExternalSSLFindingsWidget,
    ExternalHeadersFindingsWidget,
    ExternalInfrastructureFindingsWidget,
    ExternalBreachFindingsWidget
};
